import React, { FC, useState, useEffect, useCallback, useMemo, createContext, ReactNode, MouseEvent, useContext, HTMLAttributes } from "react";

import { twMerge } from "tailwind-merge";

interface TabsContextType {
  activeKey: any;
  onSelect: (eventKey: any, event: MouseEvent | null) => void;
  mountOnEnter: boolean;
  unmountOnExit: boolean;
}

export const TabsContext = createContext<TabsContextType>({
  activeKey: null,
  onSelect: () => {},
  mountOnEnter: false,
  unmountOnExit: false,
});

interface TabsProps {
  /** The currently active tab key */
  activeKey?: any;
  /** The default active tab key, if uncontrolled */
  defaultActiveKey?: any;
  /** Callback fired when a tab is selected */
  onSelect?: (eventKey: any, event: MouseEvent | null) => void;
  /** Wait until the first "enter" to mount tabs (add them to the DOM) */
  mountOnEnter?: boolean;
  /** Unmount tabs (remove them from the DOM) when they are no longer visible */
  unmountOnExit?: boolean;
  /** Additional CSS classes to apply */
  className?: string;
  /** Variant of the tabs style ('lifted' is default) */
  variant?: "lifted" | "bordered" | "boxed";
  /** Content of the Tabs, should be `Tab` components */
  children?: ReactNode;
}

/**
 * **Tabs Component**
 *
 * Manages active state and renders tabs and their content.
 * Should contain `Tab` components as children.
 */
export const Tabs: FC<TabsProps> = ({
  activeKey: controlledActiveKey,
  defaultActiveKey,
  onSelect,
  mountOnEnter = false,
  unmountOnExit = false,
  className,
  variant = "bordered",
  children,
  ...props
}) => {
  const [activeKey, setActiveKey] = useState(controlledActiveKey ?? defaultActiveKey);

  useEffect(() => {
    if (controlledActiveKey !== undefined) {
      setActiveKey(controlledActiveKey);
    }
  }, [controlledActiveKey]);

  const handleSelect = useCallback(
    (eventKey: any, event: MouseEvent | null) => {
      if (onSelect) {
        onSelect(eventKey, event);
      }
      if (controlledActiveKey === undefined) {
        setActiveKey(eventKey);
      }
    },
    [onSelect, controlledActiveKey],
  );

  const contextValue = useMemo(
    () => ({
      activeKey,
      onSelect: handleSelect,
      mountOnEnter,
      unmountOnExit,
    }),
    [activeKey, handleSelect, mountOnEnter, unmountOnExit],
  );

  const tabsClasses = twMerge(
    "si-tabs flex flex-wrap justify-normal",
    variant === "lifted" && "si-tabs-lifted [--tab-color:--color-primary]",
    variant === "bordered" && "si-tabs-bordered",
    variant === "boxed" && "si-tabs-boxed",
    className,
  );

  const tabList = React.Children.map(children, (child) => {
    if (!React.isValidElement(child)) return null;

    const { eventKey, title, disabled, tabClassName, className: tabClassNameProp } = child.props;
    const isActive = eventKey === activeKey;

    const tabButtonClasses = twMerge(
      "si-tab text-nowrap text-sm font-normal min-h-8 bg-transparent focus-visible:outline-primary",
      isActive && "si-tab-active font-semibold",
      disabled && "si-tab-disabled",
      variant === "bordered" && "transition-all hover:[--fallback-bc:--color-primary]",
      isActive && variant === "bordered" && "[--fallback-bc:--color-primary]",
      variant === "boxed" && "border-none transition-all",
      tabClassName,
      tabClassNameProp,
    );

    const handleClick = (event: MouseEvent) => {
      if (disabled) return;
      handleSelect(eventKey, event);
    };

    return (
      <button type="button" role="tab" key={eventKey} aria-selected={isActive} aria-disabled={disabled} className={tabButtonClasses} onClick={handleClick}>
        {title}
      </button>
    );
  });

  return (
    <TabsContext.Provider value={contextValue}>
      <div {...props}>
        <div role="tablist" className={tabsClasses}>
          {tabList}
        </div>
        <div className="si-tab-content">{children}</div>
      </div>
    </TabsContext.Provider>
  );
};

interface TabProps extends Omit<HTMLAttributes<HTMLDivElement>, "title" | "disabled"> {
  /** Uniquely identifies the Tab among its siblings */
  eventKey: any;
  /** The content displayed in the tab title */
  title: ReactNode;
  /** Whether the Tab is disabled */
  disabled?: boolean;
  /** Class name applied to the tab title */
  tabClassName?: string;
  /** Additional class names */
  className?: string;
  /** Content of the Tab pane */
  children?: ReactNode;
}

/**
 * **Tab Component**
 *
 * Represents a single tab pane and its content.
 * Should be used within a `Tabs` component.
 */
export const Tab: FC<TabProps> = ({ eventKey, title, disabled, tabClassName, className, children, ...rest }) => {
  const { activeKey, mountOnEnter, unmountOnExit } = useContext(TabsContext);
  const isActive = eventKey === activeKey;

  const [mounted, setMounted] = useState(!mountOnEnter || isActive);

  useEffect(() => {
    if (isActive && !mounted) {
      setMounted(true);
    }
  }, [isActive, mounted]);

  if (unmountOnExit && !isActive) {
    return null;
  }

  if (mountOnEnter && !mounted) {
    return null;
  }

  return (
    <div {...rest} hidden={!isActive} role="tabpanel" className={className}>
      {children}
    </div>
  );
};
